import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNOK, formatPercent } from "@/lib/format";
import type { FeilbudsjetteringResult } from "@/lib/dashboard-bf-calc";
import type { SellerAccuracyEntry } from "@/lib/dashboard-panels-calc";
import { TONE_TEXT } from "@/lib/widget-status";

interface FeilbudsjetteringPanelProps {
  total: FeilbudsjetteringResult;
  sellers: SellerAccuracyEntry[];
  nameOf: (userId: string) => string;
  isLoading?: boolean;
}

/** Trend icon: lower |deviation| than last period = improvement (green down). */
function Trend({ cur, prev }: { cur: number | null; prev: number | null }) {
  if (cur == null || prev == null) return <Minus className="h-4 w-4 text-muted-foreground" />;
  const d = Math.abs(cur) - Math.abs(prev);
  if (d < 0) return <TrendingDown className={`h-4 w-4 ${TONE_TEXT.reached}`} />;
  if (d > 0) return <TrendingUp className={`h-4 w-4 ${TONE_TEXT.miss}`} />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

/**
 * F2.2 Feilbudsjettering — company-level + per-seller budget deviation (%) with
 * trend vs the previous period. Lower |deviation| is better (rewards precise
 * budgets). Sellers sorted most-precise first by calcSellerBudgetAccuracy.
 */
export function FeilbudsjetteringPanel({ total, sellers, nameOf, isLoading }: FeilbudsjetteringPanelProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Feilbudsjettering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="h-32 animate-pulse rounded bg-muted" />
        ) : (
          <>
            <div className="flex items-baseline justify-between border-b pb-2">
              <span className="text-sm text-muted-foreground">Totalt avvik</span>
              <span className="text-lg font-semibold tabular-nums">
                {total.deviationPct == null ? "—" : formatPercent(total.deviationPct)}
              </span>
            </div>
            {sellers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen fullførte prosjekter i perioden.</p>
            ) : (
              <ul className="space-y-1">
                {sellers.map((s) => (
                  <li key={s.userId} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{nameOf(s.userId)}</span>
                    <span className="flex items-center gap-2 tabular-nums">
                      <span className="text-muted-foreground">{s.projectCount} pr.</span>
                      <span className="w-14 text-right">
                        {s.deviationPct == null ? "—" : formatPercent(s.deviationPct)}
                      </span>
                      <Trend cur={s.deviationPct} prev={s.prevDeviationPct} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              Estimert {formatNOK(total.bfEstimatedKr)} · reelt {formatNOK(total.bfActualKr)}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
